// Constraints on the plot (todo/11): context menus on axes, categories, and the plot background, selection of
// categories, marks of the constraints, long-press on touch. DOM only; the state and its rules are in
// constraint-state.js. The hooks window.psContextMenu, window.psCategoryClick, and window.psDecorate are called by
// lib/d3.parsets.js; unset (paper renders), the plot has no menus and no marks.
import { blocksOf, isContiguous, linkBlock, moveFirst, orderState, pinAll, releaseAll, releaseAxis, releaseBlocks, releasePins, setOrder } from './constraint-state.js';
import { entropyFirstAttribute } from './optimizers/entropy.js';
import ICONS from './constraint-icons.json';

const LONG_PRESS_MS = 500;
const LONG_PRESS_MOVE = 8;     // pixels; a larger movement is a drag
const SVGNS = 'http://www.w3.org/2000/svg';

// Icon of constraint-icons.json (todo/23) as SVG paths in the accent color, appended to parent; with halo, a white
// copy below keeps it legible on the ribbons.
function appendIcon(parent, name, halo = false) {
    for (const white of halo ? [true, false] : [false]) {
        for (const p of ICONS.icons[name]) {
            const e = document.createElementNS(SVGNS, 'path');
            const color = white ? '#fff' : ICONS.accent;
            e.setAttribute('d', p.d);
            e.setAttribute('fill', p.fill ? color : 'none');
            if (p.stroke || white) {
                e.setAttribute('stroke', p.fill && !white ? 'none' : color);
                e.setAttribute('stroke-width', (p.stroke || 0) + (white ? 3 : 0));
                e.setAttribute('stroke-linecap', p.cap || 'round');
                e.setAttribute('stroke-linejoin', p.join || 'round');
            }
            parent.append(e);
        }
    }
    return parent;
}

// Icon as a group in a plot group: box of the given size, top left at (x, y).
function plotIcon(parent, name, x, y, size, title) {
    const g = appendIcon(document.createElementNS(SVGNS, 'g'), name, true);
    g.setAttribute('class', 'ps-mark');
    g.setAttribute('transform', `translate(${x},${y}) scale(${size / ICONS.box})`);
    const t = document.createElementNS(SVGNS, 'title');
    t.textContent = title;
    g.append(t);
    parent.append(g);
    return g;
}

// Width of the text of an element in its font.
let measure;
function textWidth(element) {
    measure = measure || document.createElement('canvas').getContext('2d');
    const style = getComputedStyle(element);
    measure.font = `${style.fontStyle} ${style.fontWeight} ${style.fontSize} ${style.fontFamily}`;
    return measure.measureText(element.textContent).width;
}

// Icon in front of a menu label (empty slot for items without an icon, so that the labels align).
function menuIcon(name) {
    const svg = document.createElementNS(SVGNS, 'svg');
    svg.setAttribute('class', 'ps-menu-icon');
    svg.setAttribute('viewBox', `0 0 ${ICONS.box} ${ICONS.box}`);
    svg.setAttribute('aria-hidden', 'true');
    return name ? appendIcon(svg, name) : svg;
}

/**
 * context: { state() -> constraint state, config() -> configuration shown ([{ name, categories: [names] }]),
 *   dataset() -> dataset shown, show(config) -> draws a configuration, changed() -> after a change of the state
 *   that keeps the configuration, locked() -> true while the constraints must not change (an optimization) }.
 * Returns { decorate, close, clearSelection, withoutMarks }.
 */
export function initConstraintMenu(context) {
    let selection = null;      // { attribute, categories: Set, order: categories of the attribute when selected }
    let lastPointer = 'mouse';
    let suppressClick = 0;     // time of the last long-press: the click that may follow it is ignored

    const menu = document.createElement('div');
    menu.className = 'ps-menu';
    menu.style.display = 'none';
    document.body.append(menu);

    const svg = () => document.getElementById('vis-svg');
    const attributeOf = name => context.config().find(a => a.name === name);

    // --- Menu element -----------------------------------------------------------------------------------------

    // items: { label, icon (name in constraint-icons.json), run, hint (shown, and the item disabled, if set) } or 'separator'.
    function open(items, x, y) {
        if (context.locked()) items = [{ label: 'Constraints are locked', hint: 'an optimization is running; cancel it to change them' }];
        menu.replaceChildren(...items.map(item => {
            if (item === 'separator') return Object.assign(document.createElement('div'), { className: 'ps-menu-separator' });
            const button = Object.assign(document.createElement('button'), { type: 'button', className: 'ps-menu-item' });
            const label = Object.assign(document.createElement('span'), { className: 'ps-menu-label' });
            label.append(menuIcon(item.icon), item.label);
            button.append(label);
            if (item.hint) {
                button.classList.add('disabled');
                button.setAttribute('aria-disabled', 'true');
                button.append(Object.assign(document.createElement('small'), { textContent: item.hint }));
            } else {
                button.onclick = () => { close(); item.run(); };
            }
            return button;
        }));
        menu.style.display = 'block';
        menu.style.left = x + 'px';
        menu.style.top = y + 'px';
        // Keep the menu inside the window.
        const box = menu.getBoundingClientRect();
        if (box.right > window.innerWidth) menu.style.left = Math.max(window.scrollX, x - box.width) + 'px';
        if (box.bottom > window.innerHeight) menu.style.top = Math.max(window.scrollY, y - box.height) + 'px';
    }

    function close() {
        menu.style.display = 'none';
    }

    document.addEventListener('pointerdown', event => { if (!menu.contains(event.target)) close(); }, true);
    document.addEventListener('keydown', event => {
        if (event.key !== 'Escape') return;
        close();
        clearSelection();
    });
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);

    // --- Items --------------------------------------------------------------------------------------------------

    function entropyItem() {
        const state = context.state(), config = context.config();
        const name = entropyFirstAttribute(context.dataset());
        const order = moveFirst(state, config, name);
        const label = `Highest-entropy attribute first (${name})`;
        if (!order) return { label, icon: 'position', hint: `a fixed axis stands before ${name}; release it first` };
        return {
            label, icon: 'position', run: () => {
                state.positions.add(name);
                if (config[0].name === name) context.changed();
                else context.show(order.map(n => config.find(a => a.name === n)));
            },
        };
    }

    // Order actions of an attribute (as shown): fixed as shown ('left') or reversed ('right'), todo/10 Decision 6.
    function orderItems(attribute) {
        const state = context.state();
        const current = orderState(state, attribute);
        const set = value => {
            const categories = setOrder(state, attribute, value);
            if (categories.join('\u0000') === attribute.categories.join('\u0000')) context.changed();
            else context.show(context.config().map(a => (a.name === attribute.name ? { name: a.name, categories } : a)));
        };
        if (current === 'blocks') {
            const hint = 'replaced by the blocks of this axis';
            return [{ label: 'Fix order as shown', icon: 'right', hint }, { label: 'Fix reversed order', icon: 'left', hint }];
        }
        if (current === 'free') {
            return [{ label: 'Fix order as shown', icon: 'right', run: () => set('left') }, { label: 'Fix reversed order', icon: 'left', run: () => set('right') }];
        }
        return [{ label: 'Reverse fixed order', icon: 'ordinal', run: () => set(current === 'left' ? 'right' : 'left') }, { label: 'Release order', icon: 'ordinal', run: () => set('free') }];
    }

    function hasConstraint(state, attribute) {
        return state.positions.has(attribute.name) || !!state.order[attribute.name] || (state.pins[attribute.name] || new Set()).size > 0
            || blocksOf(state, attribute).length > 0;
    }

    function axisItems(name) {
        const state = context.state(), attribute = attributeOf(name);
        const fixed = state.positions.has(name);
        return [
            fixed ? { label: 'Release position', icon: 'position', run: () => { state.positions.delete(name); context.changed(); } }
                : { label: 'Fix position', icon: 'position', run: () => { state.positions.add(name); context.changed(); } },
            ...orderItems(attribute),
            entropyItem(),
            'separator',
            hasConstraint(state, attribute) ? { label: 'Release all constraints of this axis', run: () => { releaseAxis(state, name); context.changed(); } }
                : { label: 'Release all constraints of this axis', hint: 'this axis has no constraints' },
        ];
    }

    // categories: names on one attribute (the selection).
    function categoryItems(name, categories) {
        const state = context.state(), attribute = attributeOf(name);
        const pins = state.pins[name] || new Set();
        const n = categories.length;
        const items = [];
        if (!categories.every(c => pins.has(c))) items.push({ label: n > 1 ? `Pin selected (${n})` : 'Pin', icon: 'pin', run: () => { pinAll(state, name, categories); context.changed(); } });
        if (categories.some(c => pins.has(c))) items.push({ label: n > 1 ? 'Release pins' : 'Release pin', icon: 'pin', run: () => { releasePins(state, name, categories); context.changed(); } });
        const blocks = blocksOf(state, attribute);
        const link = { label: 'Link as block', icon: 'block' };
        if (n < 2) link.hint = 'select two or more adjacent categories (Ctrl-click)';
        else if (!isContiguous(attribute, categories)) link.hint = 'the selected categories are not adjacent';
        else if (blocks.some(block => categories.every(c => block.includes(c)))) link.hint = 'already one block';
        else link.run = () => { linkBlock(state, attribute, categories); context.changed(); };
        items.push(link);
        if (blocks.some(block => block.some(c => categories.includes(c)))) {
            items.push({ label: 'Release block', icon: 'block', run: () => { releaseBlocks(state, attribute, categories); context.changed(); } });
        }
        return [...items, 'separator', ...orderItems(attribute)];
    }

    function backgroundItems() {
        const state = context.state();
        const any = context.config().some(a => hasConstraint(state, a));
        return [
            entropyItem(),
            any ? { label: 'Release all constraints', run: () => { releaseAll(state); context.changed(); } } : { label: 'Release all constraints', hint: 'no constraints are set' },
        ];
    }

    // --- Selection ----------------------------------------------------------------------------------------------

    function select(name, categories) {
        selection = categories.length > 0 ? { attribute: name, categories: new Set(categories), order: attributeOf(name).categories.join('\u0000') } : null;
        decorate();
    }

    function clearSelection() {
        if (!selection) return;
        selection = null;
        decorate();
    }

    // Categories of the selection in the order shown.
    function selected(name) {
        if (!selection || selection.attribute !== name) return [];
        return attributeOf(name).categories.filter(c => selection.categories.has(c));
    }

    // --- Hooks of lib/d3.parsets.js -------------------------------------------------------------------------------

    window.psContextMenu = (kind, d, event) => {
        event.preventDefault();
        if (kind === 'axis') {
            open(axisItems(d.name), event.pageX, event.pageY);
            return;
        }
        // Right-click on a selected category acts on the selection, on another one replaces the selection.
        const name = d.dimension.name;
        if (!selected(name).includes(d.name)) select(name, [d.name]);
        open(categoryItems(name, selected(name)), event.pageX, event.pageY);
    };

    window.psCategoryClick = (d, event) => {
        if (Date.now() - suppressClick < 700) return;
        const name = d.dimension.name;
        const current = selected(name);
        const toggle = lastPointer === 'touch' || event.ctrlKey || event.metaKey || event.shiftKey;
        if (!toggle) select(name, [d.name]);
        else select(name, current.includes(d.name) ? current.filter(c => c !== d.name) : [...current, d.name]);
    };

    window.psDecorate = decorate;

    // Plot background: menu and clearing the selection; long-press on touch opens the same menus.
    const container = document.getElementById('parallel-sets');
    container.addEventListener('contextmenu', event => {
        if (event.target !== svg()) return;
        event.preventDefault();
        open(backgroundItems(), event.pageX, event.pageY);
    });
    container.addEventListener('click', event => { if (event.target === svg()) clearSelection(); });

    let press = null;
    const endPress = () => { if (press) clearTimeout(press.timer); press = null; };
    container.addEventListener('pointerdown', event => {
        lastPointer = event.pointerType;
        endPress();
        if (event.pointerType !== 'touch') return;
        const target = event.target.closest('g.category, g.dimension') || (event.target === svg() ? event.target : null);
        if (!target) return;
        const { clientX, clientY } = event;
        press = {
            x: clientX, y: clientY, timer: setTimeout(() => {
                press = null;
                suppressClick = Date.now();
                event.target.dispatchEvent(new MouseEvent('contextmenu', { bubbles: true, cancelable: true, view: window, clientX, clientY }));
            }, LONG_PRESS_MS),
        };
    }, true);
    container.addEventListener('pointermove', event => {
        if (press && Math.hypot(event.clientX - press.x, event.clientY - press.y) > LONG_PRESS_MOVE) endPress();
    }, true);
    container.addEventListener('pointerup', endPress, true);
    container.addEventListener('pointercancel', endPress, true);
    // A browser that opens its own menu on a long-press (Android) sends a contextmenu event: it replaces the timer,
    // and a second one right after the long-press is dropped.
    container.addEventListener('contextmenu', event => {
        if (!event.isTrusted) return;
        if (press) { endPress(); suppressClick = Date.now(); return; }
        if (lastPointer === 'touch' && Date.now() - suppressClick < 1000) { event.preventDefault(); event.stopPropagation(); }
    }, true);

    // --- Marks ----------------------------------------------------------------------------------------------------

    // Draws the marks of the constraints and the selection into the axis and category groups, so that they move with
    // drags and transitions. Called by lib/d3.parsets.js after every update of the categories.
    function decorate() {
        const node = svg();
        if (!node) return;
        const state = context.state();
        const plot = d3.select(node);
        plot.selectAll('.ps-mark').remove();
        if (selection) {
            const attribute = attributeOf(selection.attribute);
            if (!attribute || attribute.categories.join('\u0000') !== selection.order) selection = null;
        }
        plot.selectAll('g.dimension').each(function (dimension) {
            const name = dimension.name;
            const attribute = { name, categories: dimension.categories.map(c => c.name) };
            const label = d3.select(this).select('text.dimension');
            const fixed = state.positions.has(name);
            label.select('tspan.name').style('fill', fixed ? ICONS.accent : null);
            const marks = [];
            if (fixed) marks.push(['position', 'position fixed']);
            const order = orderState(state, attribute);
            if (order === 'left') marks.push(['right', 'category order fixed as shown']);
            if (order === 'right') marks.push(['left', 'category order fixed, reversed']);
            // A spacer (em space) in the label keeps the room of each icon; the icon is drawn over it in the axis group.
            // The name is measured on a canvas, since the plot may be hidden (no text layout).
            const spacers = marks.map(() => label.insert('tspan', 'tspan.sort.alpha').attr('class', 'ps-mark').attr('dx', '0.3em').text('\u2003'));
            if (marks.length > 0) {
                const size = parseFloat(getComputedStyle(spacers[0].node()).fontSize);
                const start = textWidth(label.select('tspan.name').node());
                marks.forEach(([icon, title], i) => plotIcon(this, icon, start + (1.3 * i + 0.3) * size, -25 - 0.85 * size, size, title));
            }
            const pins = state.pins[name] || new Set();
            const blocks = blocksOf(state, attribute);
            const categories = dimension.categories;
            d3.select(this).selectAll('g.category').each(function (category) {
                const g = d3.select(this);
                g.classed('selected', !!selection && selection.attribute === name && selection.categories.has(category.name));
                if (pins.has(category.name)) {
                    // Pushpin, its tip on the right end of the category.
                    const size = 18, s = size / ICONS.box;
                    plotIcon(this, 'pin', Math.max(12 * s, category.dx - 7) - 12 * s, -1 - 23.5 * s, size, 'pinned at this position');
                }
                const block = blocks.find(b => b.includes(category.name));
                if (!block) return;
                // Bracket below the block (|_|, as the block icon), one segment per category, reaching to the next
                // member of the block, so that it moves with drags; white halo on the ribbons.
                const i = block.indexOf(category.name), last = i === block.length - 1;
                const next = categories.find(c => c.name === block[i + 1]);
                const width = last ? category.dx - 1 : next.x - category.x;
                const d = (i === 0 ? 'M1,3V10' : 'M0,10') + 'H' + width + (last ? 'V3' : '');
                const segment = g.append('g').attr('class', 'ps-mark ps-block');
                segment.append('path').attr('d', d).attr('fill', 'none').attr('stroke', '#fff').attr('stroke-width', 5.5);
                segment.append('path').attr('d', d).attr('fill', 'none').attr('stroke', ICONS.accent).attr('stroke-width', 2.5);
                segment.append('title').text('block: ' + block.join(', '));
            });
        });
    }

    // Runs an export without the marks and the selection outline (todo/11 Decision 8); the source is taken
    // synchronously, then the marks are drawn again.
    function withoutMarks(run) {
        const node = svg();
        if (node) {
            node.querySelectorAll('.ps-mark').forEach(e => e.remove());
            node.querySelectorAll('tspan.name').forEach(e => e.style.removeProperty('fill'));
            node.querySelectorAll('.selected').forEach(e => e.classList.remove('selected'));
        }
        try {
            return run();
        } finally {
            decorate();
        }
    }

    return { decorate, close, clearSelection, withoutMarks };
}
